import React from "react";
import { noteKey } from "../../lib/jianpu";

// On-screen picture of the EasyPlay 1s, matching the real thing: fifteen big
// round buttons in three rows of five, reading the major scale left to right,
// TOP to bottom — low do at the top left, high do (blue) at the bottom right.
// Ten small gray sharp buttons sit in the gaps between rows. Landmarks match
// the hardware: red = low 1, dark = the two 5s ("heart" and "invader"),
// yellow = middle 1, blue = high 1.
//
// Props:
//   target  — single { degree, octave, accidental } to pulse (melody practice)
//   targets — array of cells to light together (chord shapes)
//   pressed — Set of noteKey strings currently held / collected
//   hitKey  — noteKey to flash green briefly (last correct press)
//   onPress — optional (cell) => void; makes the pads clickable

// Big buttons by row; grid columns are twentieths (a big button spans 4).
const BIG_ROWS = [
  [
    { degree: 1, octave: 0 },
    { degree: 2, octave: 0 },
    { degree: 3, octave: 0 },
    { degree: 4, octave: 0 },
    { degree: 5, octave: 0 },
  ],
  [
    { degree: 6, octave: 0 },
    { degree: 7, octave: 0 },
    { degree: 1, octave: 1 },
    { degree: 2, octave: 1 },
    { degree: 3, octave: 1 },
  ],
  [
    { degree: 4, octave: 1 },
    { degree: 5, octave: 1 },
    { degree: 6, octave: 1 },
    { degree: 7, octave: 1 },
    { degree: 1, octave: 2 },
  ],
];

// Small sharp buttons: `pos` n means "between big buttons n and n+1 of the
// row below"; -1 hangs off the row's left edge (the wrap-around sharp, e.g.
// #5 between the top row's 5 and the middle row's 6).
const SMALL_ROWS = [
  [
    { degree: 1, octave: 0, accidental: 1, pos: 0 },
    { degree: 2, octave: 0, accidental: 1, pos: 1 },
    { degree: 4, octave: 0, accidental: 1, pos: 3 },
  ],
  [
    { degree: 5, octave: 0, accidental: 1, pos: -1 },
    { degree: 6, octave: 0, accidental: 1, pos: 0 },
    { degree: 1, octave: 1, accidental: 1, pos: 2 },
    { degree: 2, octave: 1, accidental: 1, pos: 3 },
  ],
  [
    { degree: 4, octave: 1, accidental: 1, pos: 0 },
    { degree: 5, octave: 1, accidental: 1, pos: 1 },
    { degree: 6, octave: 1, accidental: 1, pos: 2 },
  ],
];

const LANDMARKS = {
  "1:0": "red",
  "5:0": "dark",
  "1:1": "yellow",
  "5:1": "dark",
  "1:2": "blue",
};

const Dots = ({ count }) => (
  <span className="jp-pad-dots">
    {Array.from({ length: count }, (_, i) => (
      <span key={i} className="jp-dot" />
    ))}
  </span>
);

export default function EasyPlayGrid({ target, targets, pressed, onPress, hitKey }) {
  const targetKeys = new Set(
    (targets || (target ? [target] : [])).map((cell) => noteKey(cell))
  );

  const hint =
    target && target.accidental
      ? `next: the small ♯${target.degree} button`
      : "";

  const pad = (cell, gridRow, colStart, small) => {
    const key = noteKey(cell);
    const cls = ["jp-pad"];
    if (small) cls.push("jp-pad--small");
    const landmark = LANDMARKS[key];
    if (landmark) cls.push(`jp-pad--${landmark}`);
    if (key === hitKey) cls.push("jp-pad--hit");
    else if (targetKeys.has(key)) cls.push("jp-pad--target");
    if (pressed?.has(key)) cls.push("jp-pad--pressed");
    return (
      <button
        key={key}
        type="button"
        className={cls.join(" ")}
        style={{ gridRow, gridColumn: `${colStart} / span ${small ? 2 : 4}` }}
        onClick={onPress ? () => onPress(cell) : undefined}
        aria-label={`${cell.accidental ? "sharp " : ""}${cell.degree} octave ${cell.octave}`}
      >
        {!small && <Dots count={cell.octave} />}
        <span>
          {cell.accidental === 1 ? "♯" : ""}
          {cell.degree}
        </span>
        {small && cell.octave > 0 && <Dots count={cell.octave} />}
      </button>
    );
  };

  return (
    <div>
      <div className="jp-grid20" role="group" aria-label="EasyPlay buttons">
        {SMALL_ROWS.map((row, r) =>
          row.map((cell) =>
            pad(cell, r * 2 + 1, cell.pos === -1 ? 1 : cell.pos * 4 + 4, true)
          )
        )}
        {BIG_ROWS.map((row, r) =>
          row.map((cell, i) => pad(cell, r * 2 + 2, i * 4 + 1, false))
        )}
      </div>
      <div className="jp-grid-hint">{hint}</div>
    </div>
  );
}
